import moment from 'moment'
import type { Money } from 'src/feature/CardDetailHero/cardDetailHeroDemoData'
import { toDisplayAmount } from 'src/feature/CardDetailHero/cardDetailHeroFormat'
import type { CurrencyFormatter } from 'src/localization/currencyFormatter'

/**
 * Display formatting for the Overview stats roll-up (EX-40632).
 *
 * Money crosses this boundary as `Money` (minor units + ISO-4217 currency) and
 * converts at the display edge via `toDisplayAmount`, which scales per currency.
 * A hardcoded ÷100 would misprice zero-decimal (JPY) and three-decimal (BHD)
 * currencies — do not reintroduce one.
 *
 * The digit count has to be passed explicitly for the same reason:
 * `createCurrencyFormatter` pins `minimumFractionDigits` to
 * `maximumFractionDigits`, so leaving it unset reintroduces the hardcode one
 * layer down. It comes from the same per-currency source `toDisplayAmount`
 * scales by, so the two can never disagree.
 */

/**
 * Whole-unit amounts — every headline figure in the roll-up ($7,700, $50,000,
 * $4,400), per the design.
 *
 * Zero-decimal currencies are unaffected; for 2- and 3-decimal currencies this
 * ROUNDS for display. That is a deliberate density choice for a summary tile,
 * not a precision claim, and it applies to the payment-due figure too — a user
 * reconciling an exact amount to the cent goes to the Statements tab, which
 * formats with `currencyFractionDigits`. If that ever stops being true, this is
 * the call to revisit, not the tile's layout.
 */
export const formatStatAmount = (
  formatCurrency: CurrencyFormatter,
  money: Money,
): string =>
  formatCurrency({
    amount: toDisplayAmount(money),
    currency: money.currency as Parameters<CurrencyFormatter>[0]['currency'],
    maximumFractionDigits: 0,
  })

/** "Jul 15, 2026" — the payment-due line, where the year matters. */
export const formatStatDate = (isoDate: string): string =>
  moment(isoDate).format('MMM D, YYYY')

/**
 * "Jun 30" — the statement-close headline. The year is dropped because the
 * value is the tile's headline figure and the cycle is the current one; a
 * four-digit year there reads as noise beside "$50,000".
 */
export const formatStatDateShort = (isoDate: string): string =>
  moment(isoDate).format('MMM D')

/**
 * "15.4%" — one decimal place, matching the design.
 *
 * One decimal is meaningful here: whole percent would collapse the low
 * single-digit utilizations that are common early in a cycle down to "0%".
 */
export const formatUtilization = (percent: number): string =>
  `${Math.round(percent * 10) / 10}%`
